import React, { useState } from "react";
import { ArrowLeft, ArrowRight, CalendarCheck, CheckCircle2, ChevronDown, Loader2, Pencil, RotateCcw } from "lucide-react";
import { money } from "../../lib/packageLogic";
import { hasPrice } from "../package/QuotePanel";

const Expander = ({ title, count, children, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={testId}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="font-display font-bold text-navy text-sm">
          {title}
          {count !== undefined && <span className="text-slate-400 font-sans font-semibold ml-2">({count})</span>}
        </span>
        <ChevronDown size={17} className={`text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 border-t border-slate-100 pt-4" data-testid={`${testId}-content`}>{children}</div>}
    </div>
  );
};

// Stage 4. What they get, what it costs, and the answers it was built from — with
// a way back into any of them. Every figure comes from the pricing engine.
export default function PriceStage({
  quote,
  quoting,
  error,
  sections = [],
  idsByName = {},
  onEdit,
  onBack,
  onContinue,
  onBook,
  onStartOver,
  disclaimer,
  summaryHeading,
}) {
  if (quoting && !quote) {
    return (
      <div className="py-20 text-center" data-testid="pricing-price-loading">
        <Loader2 size={24} className="animate-spin text-orange mx-auto" />
        <p className="text-sm text-slate-500 mt-4">Working out your price…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-orange/40 bg-orange/5 p-6" data-testid="pricing-price-error">
        <p className="text-sm text-navy leading-relaxed">{error}</p>
        <button type="button" onClick={onBack} data-testid="pricing-price-error-back" className="mt-4 inline-flex items-center gap-2 text-slate-500 hover:text-navy font-semibold text-sm">
          <ArrowLeft size={15} /> Back to the questions
        </button>
      </div>
    );
  }

  if (quote?.status === "consultation" || (quote && !hasPrice(quote))) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8" data-testid="pricing-price-consultation">
        <h2 className="font-display font-extrabold text-navy text-xl md:text-2xl">
          {quote.consultation_title || "Your requirements need a proper conversation"}
        </h2>
        <p className="text-sm text-slate-600 mt-3 leading-relaxed max-w-2xl">
          {quote.consultation_message ||
            "There's enough going on here that a form can't price it fairly. Everything you've told us comes with us to the call, so nothing is wasted."}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button type="button" onClick={onBook} data-testid="pricing-consultation-book" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5">
            <CalendarCheck size={16} /> Book a Discovery Call
          </button>
          <button type="button" onClick={onContinue} data-testid="pricing-consultation-continue" className="inline-flex items-center gap-2 border border-slate-300 hover:border-navy text-navy font-bold px-7 py-3.5 rounded-full transition-colors">
            Send us your details
          </button>
        </div>
      </div>
    );
  }

  if (!quote) {
    // No price, nothing loading and no error — a resumed session that lost its
    // quote. Never leave them looking at an empty page.
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8" data-testid="pricing-price-empty">
        <h2 className="font-display text-xl font-extrabold text-navy">Let's pick that back up</h2>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600">
          We haven't got a price to show yet — a couple of answers are still outstanding. Your selections are safe, so it's a short hop
          back.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            data-testid="pricing-price-empty-back"
            className="inline-flex items-center gap-2 rounded-full bg-orange px-7 py-3.5 font-bold text-white duration-300 [transition-property:transform,background-color] hover:-translate-y-0.5 hover:bg-orange-dark"
          >
            Back to the questions <ArrowRight size={16} />
          </button>
          <button
            type="button"
            onClick={onBook}
            data-testid="pricing-price-empty-book"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-7 py-3.5 font-bold text-navy duration-200 [transition-property:border-color] hover:border-navy"
          >
            <CalendarCheck size={15} /> Book a Discovery Call instead
          </button>
        </div>
      </div>
    );
  }

  const totals = quote.totals || {};
  const lines = quote.services || [];
  const recurring = lines.filter((l) => l.recurring);
  const oneOff = lines.filter((l) => !l.recurring);
  const alignment = Number(totals.fee_alignment_total || 0);

  return (
    <div className="space-y-5" data-testid="pricing-price-stage">
      {/* The figure */}
      <div className="rounded-3xl bg-navy text-white p-8 md:p-10 text-center" data-testid="pricing-price-hero">
        <span className="inline-block text-[10px] font-bold uppercase tracking-widest bg-orange/20 text-orange px-2.5 py-1 rounded">Indicative</span>
        <p className="text-sm text-slate-300 mt-5">Your monthly fee</p>
        <p className="font-display font-extrabold text-5xl md:text-6xl mt-1 tracking-tight" data-testid="pricing-monthly-fee">
          {money(totals.monthly_recurring)}
        </p>
        <p className="text-sm text-slate-300 mt-1">per month + VAT</p>
        {totals.one_off > 0 && (
          <p className="mt-6 inline-block rounded-full bg-white/10 px-5 py-2.5 text-sm font-semibold" data-testid="pricing-one-off-pill">
            One-off setup fee: {money(totals.one_off)} + VAT
          </p>
        )}
      </div>

      {/* What they get */}
      {recurring.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6" data-testid="pricing-includes">
          <h3 className="font-display font-bold text-navy text-base md:text-lg">Your CASS package includes</h3>
          <ul className="mt-4 space-y-3">
            {recurring.map((l) => (
              <li key={l.name} className="flex items-start gap-3" data-testid={`pricing-includes-${l.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <CheckCircle2 size={16} className="text-teal shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-navy">{l.name}</p>
                  {l.category && <p className="text-xs text-slate-500 mt-0.5">{l.category}</p>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* One-off work, named but not individually priced — the one-off total is
          shown with the monthly figure above. Individual item fees are not
          disclosed on the website. */}
      {(oneOff.length > 0 || alignment > 0) && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6" data-testid="pricing-one-off">
          <h3 className="font-display font-bold text-navy text-base md:text-lg">Included in your one-off setup fee</h3>
          <p className="text-xs text-slate-500 mt-1">Charged once, separately from your monthly fee.</p>
          <ul className="mt-4 space-y-3">
            {oneOff.map((l) => (
              <li key={l.name} className="flex items-start gap-3 text-sm">
                <CheckCircle2 size={16} className="text-teal shrink-0 mt-0.5" />
                <span className="text-navy font-semibold">{l.name}</span>
              </li>
            ))}
            {alignment > 0 && (
              <li className="flex items-start gap-3 text-sm" data-testid="pricing-alignment">
                <CheckCircle2 size={16} className="text-teal shrink-0 mt-0.5" />
                <span className="text-navy font-semibold">Fee alignment</span>
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Everything they told us, with a way back to change it */}
      <Expander title={summaryHeading || "Your answers"} count={lines.reduce((n, l) => n + (l.params || []).length, 0)} testId="pricing-answers-toggle">
        <div className="space-y-5">
          <div>
            <button
              type="button"
              onClick={() => onEdit("business")}
              data-testid="pricing-edit-business"
              className="text-xs font-bold text-orange hover:text-orange-dark inline-flex items-center gap-1.5"
            >
              <Pencil size={11} /> About your business
            </button>
          </div>
          {lines.filter((l) => (l.params || []).length > 0).map((l) => {
            const id = idsByName[l.name];
            const index = sections.findIndex((s) => s.service.service_id === id);
            return (
              <div key={l.name} data-testid={`pricing-answers-${l.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-bold text-navy">{l.name}</p>
                  {index >= 0 && (
                    <button
                      type="button"
                      onClick={() => onEdit(index)}
                      data-testid={`pricing-edit-${id}`}
                      className="text-xs font-bold text-orange hover:text-orange-dark inline-flex items-center gap-1.5 shrink-0"
                    >
                      <Pencil size={11} /> Change
                    </button>
                  )}
                </div>
                <dl className="mt-2 space-y-1.5">
                  {(l.params || []).map((p) => (
                    <div key={p.label} className="flex items-baseline justify-between gap-4 text-xs">
                      <dt className="text-slate-500">{p.label}</dt>
                      <dd className="text-navy font-semibold text-right shrink-0">{p.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            );
          })}
        </div>
      </Expander>

      {disclaimer && (
        <p className="text-xs text-slate-500 leading-relaxed px-1" data-testid="pricing-disclaimer">{disclaimer}</p>
      )}

      <div className="flex flex-wrap items-center gap-3 pt-1">
        <button type="button" onClick={onBook} data-testid="pricing-book-call" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5">
          Book a Discovery Call <ArrowRight size={16} />
        </button>
        <button type="button" onClick={onContinue} data-testid="pricing-talk-it-through" className="inline-flex items-center gap-2 border border-slate-300 hover:border-navy text-navy font-bold px-8 py-4 rounded-full transition-colors">
          Talk it through with us
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-5 pt-2">
        <button type="button" onClick={onBack} data-testid="pricing-price-back" className="inline-flex items-center gap-2 text-slate-500 hover:text-navy font-semibold text-sm transition-colors">
          <ArrowLeft size={15} /> Back to the questions
        </button>
        <button type="button" onClick={onStartOver} data-testid="pricing-start-again" className="inline-flex items-center gap-2 text-slate-500 hover:text-navy font-semibold text-sm transition-colors">
          <RotateCcw size={14} /> Start again
        </button>
      </div>
    </div>
  );
}
